// Shared soft-cap response detector + toast builder.
//
// Edge functions wrapped in runWithCreditGuard return HTTP 200 with a
// payload of { capped: true, code, message } when the agency is at lead
// cap, out of credits, or suspended. Tools that don't check for this
// flag end up casting the cap payload to their result type and rendering
// a broken UI (empty score, missing fields, misleading "not a restaurant"
// banner, etc.). All free-tool invoke sites should call isCappedResponse
// on the response data before consuming it as a real result.
//
// Why a shared helper: keeps the wording consistent across the 10 free
// tools and ensures any future cap code (e.g. RATE_LIMITED) only needs
// to be wired up here.

export type CappedCode =
  | 'LEAD_CAP_REACHED'
  | 'NO_CREDITS'
  | 'AGENCY_SUSPENDED'
  | 'TRIAL_EXPIRED'
  // A per-tool monthly ceiling. This union is the wiring the file's own header
  // asks for, and leaving it out is not a type error: the guard's new code fell
  // through to the catch-all below, so an agency owner hitting a limit on their
  // own dashboard read "Reports temporarily unavailable", copy written for a
  // visitor on someone else's white-label embed.
  | 'TOOL_MONTHLY_CAP'
  // The healthcare screening guard's own out-of-balance code. It comes from a
  // different guard (healthcare-scan-guard.ts) and was never listed here.
  | 'NO_SCANS'
  // The embed domain gate: this tool is not enabled for the site the run came
  // from. NOT EMITTED YET. with-credit-guard currently only logs the mismatch,
  // because the value it can compare (source_url) is the fly-social.com embed
  // page rather than the customer's parent site, so enforcing today would
  // refuse every legitimate embed run. Wired up in advance so that turning
  // enforcement on is a one-line change there and not a second bug here.
  | 'DOMAIN_NOT_ALLOWED';

export interface CappedResponse {
  capped: true;
  code?: CappedCode;
  message?: string;
}

export function isCappedResponse(data: unknown): data is CappedResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { capped?: unknown }).capped === true
  );
}

interface EmbedLike {
  isEmbed: boolean;
  agencyName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
}

export function cappedToast(
  data: CappedResponse,
  embed: EmbedLike,
): { title: string; description: string; variant: 'destructive' } {
  const isNoCredits = data.code === 'NO_CREDITS';
  const isSuspended = data.code === 'AGENCY_SUSPENDED';
  const isTrialExpired = data.code === 'TRIAL_EXPIRED';
  const brand = embed.isEmbed ? (embed.agencyName || 'the agency') : 'the team';
  const contactBits: string[] = [];
  if (embed.contactEmail) contactBits.push(embed.contactEmail);
  if (embed.contactPhone) contactBits.push(embed.contactPhone);

  // Trial-expired case has its own copy. The visitor is on an agency's
  // embed and the agency hasn't upgraded yet — message keeps them
  // informed without exposing internal billing language.
  if (isTrialExpired) {
    return {
      title: 'This tool is paused',
      description: contactBits.length > 0
        ? `${brand} hasn't reactivated this tool yet. Please reach out: ${contactBits.join(' · ')}`
        : `${brand} hasn't reactivated this tool yet. Please reach out directly.`,
      variant: 'destructive',
    };
  }

  // A per-tool ceiling, which is a different situation from having no scans at
  // all: every other tool still runs. On an embed the visitor must not be told
  // about the agency's internal limits, so they get the same neutral copy and
  // contact details as the other agency-side caps.
  if (data.code === 'TOOL_MONTHLY_CAP') {
    if (embed.isEmbed) {
      return {
        title: 'This tool is temporarily unavailable',
        description: contactBits.length > 0
          ? `Please reach out to ${brand} to get this restored: ${contactBits.join(' · ')}`
          : `Please reach out to ${brand} to get this restored, or contact support.`,
        variant: 'destructive',
      };
    }
    // Not called a "monthly" limit. For the readers most likely to hit it, a
    // trialing agency, the period never rolls: current_period_start is stamped
    // once and the refill branch is gated on status='active'. Saying monthly
    // promises a reset they will never see.
    return {
      title: 'Limit reached for this tool',
      description: data.message
        || 'You have reached the limit for this tool. Every other tool still works.',
      variant: 'destructive',
    };
  }

  // Not a billing problem, so it does not get billing copy. The visitor cannot
  // act on this at all: the fix is for the agency to add this domain to the
  // tool's allowed list. Say what is true without implying anyone is out of
  // scans, and give them the agency's contact details on an embed.
  if (data.code === 'DOMAIN_NOT_ALLOWED') {
    return {
      title: 'This tool is not available on this site',
      description: contactBits.length > 0
        ? `${brand} has not enabled this tool for this website. Please reach out: ${contactBits.join(' · ')}`
        : `${brand} has not enabled this tool for this website.`,
      variant: 'destructive',
    };
  }

  // NO_SCANS is the healthcare screening guard's own out-of-balance code. It is
  // the same situation as NO_CREDITS for the reader, and without it here it fell
  // to the catch-all below and leaked the server's ops sentence.
  if (isNoCredits || isSuspended || data.code === 'NO_SCANS') {
    return {
      title: 'This tool is temporarily unavailable',
      description: contactBits.length > 0
        ? `Please reach out to ${brand} to get this restored: ${contactBits.join(' · ')}`
        : `Please reach out to ${brand} to get this restored, or contact support.`,
      variant: 'destructive',
    };
  }

  // data.message is written for the AGENCY, by the server. For no_credits it
  // reads "This agency has run out of scans. Top up or upgrade the plan to keep
  // going." Passing it through on an embed shows a visitor on a client's own
  // white-label domain the agency's internal billing state, and tells them to
  // top up an account that is not theirs.
  //
  // So the server's wording is only ever used off-embed, on the agency's own
  // dashboard, where it is addressed to the right person. Embed visitors always
  // get the neutral copy and the agency's contact details.
  if (embed.isEmbed) {
    return {
      title: 'Reports temporarily unavailable',
      description: contactBits.length > 0
        ? `Free reports are paused right now. Please reach out to ${brand} at ${contactBits.join(' / ')}.`
        : `Free reports are paused right now. Please reach out to ${brand} directly.`,
      variant: 'destructive',
    };
  }

  return {
    title: 'Reports temporarily unavailable',
    description: data.message
      || `Free reports are paused right now. Please reach out to ${brand} directly.`,
    variant: 'destructive',
  };
}
